/**
 * EditorialCallout — accent LEFT-rule callout, THEME-ADAPTIVE. The accent
 * SEMANTICS (tone → rule colour) are constant across presets; the CHROME
 * density switches by `theme.headingScale` (+ `theme.density`):
 *
 *   - editorial (Journal / Quarterly / Monograph): a left-accent rule with
 *     generous padding — a restrained journal callout, NOT a saturated
 *     blue/slate card. (Reference: the callout treatment in
 *     docs/design/journal-report-full.html.)
 *   - technical (Institutional): the same left rule, crisp at a slightly
 *     tighter inset.
 *   - compact (Review / Proceedings — IEEE/ACM density): tighter padding
 *     and a thinner rule, reading as a dense margin note.
 *
 * `theme.density === 'compact'` tightens padding one extra notch.
 *
 * FLATTEN TO CREAM (Decision A, 2026-06-16) — the soft accent wash that
 * previously filled the callout body is GONE globally: every callout now reads
 * ink-on-cream with the tone-coloured LEFT RULE as the sole accent. The label
 * + rule read roleColor(theme, <tone>); body reads roleColor(theme,'body').
 * A `tone` prop retints the rule to positive/warning/negative. The DOM shape,
 * the `role="note"` semantics, and the label/children/tone/id/className/style
 * API are UNCHANGED — only the background fill was removed.
 */
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import {
  getThemeReaderMode,
  roleColor,
  type ReportColorRole,
} from '../../../../lib/reportRoleColors';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

export type EditorialCalloutTone =
  | 'accent'
  | 'positive'
  | 'warning'
  | 'negative'
  /** GC-W3 — the golden NAMED, FILLED callout boxes. `caveat` = the pale-blue
   *  "PLANNING CAVEAT" / "AIR PERMITTING" note; `assumption` = the salmon,
   *  red-left-border "ASSUMPTION · TO BE CONFIRMED" box. Unlike the four legacy
   *  tones (flat per Decision A), these two carry a tinted FILL — their golden
   *  signature. */
  | 'caveat'
  | 'assumption';

export interface EditorialCalloutProps {
  theme: ReportTheme;
  children: React.ReactNode;
  /** Optional small-caps mono label rendered above the body. */
  label?: React.ReactNode;
  /** Rule + wash tone. Defaults to 'accent'. */
  tone?: EditorialCalloutTone;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  /**
   * Author an explicit, semantic page seam immediately after this bounded
   * callout. The measured paginator may use the seam to move a section opener
   * plus this complete callout into a preceding blank tail while leaving the
   * remaining section content on a labelled continuation page.
   */
  pagedSplitAfter?: boolean;
}

const RULE_ROLE: Partial<Record<EditorialCalloutTone, ReportColorRole>> = {
  accent: 'accent',
  positive: 'positive',
  warning: 'warning',
  negative: 'negative',
};
// DEFECT 1b / Decision A — the soft accent wash is GONE for the FOUR legacy
// tones: they read ink-on-cream with the tone-coloured left rule as the sole
// accent. Status semantics still read via the left-rule colour.
//
// GC-W3 — the golden NAMED callouts are the deliberate EXCEPTION: a tinted FILL
// is their signature. Reports are LIGHT-themed, so these use fixed
// light-context colours (semantic note styling, not brand chrome) matching the
// golden reference — caveat = calm info-blue; assumption = salmon w/ a red rule.
// Rule colour DOUBLES as the label text colour, so it must clear WCAG-AA (4.5:1)
// at the 12px label size on its own pale wash: assumption #a8462b ≈ 5.2:1 on
// #fbf0ec; caveat #356391 ≈ 5.6:1 on #eef3f9 (the lighter #bb5438/#3f6ea3 were
// borderline/failing as label text).
//
// DARK READER (post-`53553cbf`): the "Reports are LIGHT-themed" premise above is
// no longer true — the READING surface follows the app light/dark toggle (export
// stays pinned light). These fixed pale fills therefore rendered a near-white box
// on the dark page while the body text switched to the on-dark ramp: light ink on
// a light wash, i.e. unreadable. On dark we instead use a low-alpha tint of the
// tone hue, which composites over whatever the dark surface is — the same
// mechanism `roleColor` already uses for the `*Soft` wash roles — and take the
// rule/label from the on-dark ramp so it still clears the WCAG floor.
const NAMED_FILL: Partial<Record<EditorialCalloutTone, { bg: string; rule: string }>> = {
  caveat: { bg: '#eef3f9', rule: '#356391' },
  assumption: { bg: '#fbf0ec', rule: '#a8462b' },
};

/** Dark-mode equivalents of NAMED_FILL: tone-hue wash + on-dark rule/label. */
const NAMED_FILL_DARK_ROLE: Partial<
  Record<EditorialCalloutTone, { bg: ReportColorRole; rule: ReportColorRole }>
> = {
  caveat: { bg: 'accentSoft', rule: 'accent' },
  assumption: { bg: 'warningSoft', rule: 'warning' },
};

/** Resolve to a known scale, defaulting unknown/legacy values to editorial. */
function resolveScale(
  scale: ReportTheme['headingScale'] | undefined,
): ReportTheme['headingScale'] {
  return scale === 'editorial' || scale === 'technical' || scale === 'compact'
    ? scale
    : 'editorial';
}

export function EditorialCallout({
  theme,
  children,
  label,
  tone = 'accent',
  id,
  className,
  style,
  pagedSplitAfter = false,
}: EditorialCalloutProps): React.ReactElement {
  const named = NAMED_FILL[tone];
  // On dark, resolve the named tones through the role ramp instead of the fixed
  // pale hexes (see NAMED_FILL_DARK_ROLE above).
  const namedDark = getThemeReaderMode(theme) === 'dark'
    ? NAMED_FILL_DARK_ROLE[tone]
    : undefined;
  const ruleColor = namedDark
    ? roleColor(theme, namedDark.rule)
    : named
      ? named.rule
      : roleColor(theme, RULE_ROLE[tone] ?? 'accent');
  // Legacy tones stay flat (Decision A); the golden named tones carry a fill.
  const background = namedDark
    ? roleColor(theme, namedDark.bg)
    : named
      ? named.bg
      : 'transparent';
  const scale = resolveScale(theme.headingScale);
  const dense = theme.density === 'compact';

  let padding: React.CSSProperties['padding'];
  if (scale === 'compact') padding = dense ? '6px 10px' : '8px 12px';
  else if (scale === 'technical') padding = dense ? '9px 12px' : '10px 14px';
  else padding = dense ? '10px 14px' : '12px 16px';

  // 14px unified report BODY size (text-sm) — the callout body reads the
  // same size as prose, table cells, and the other editorial block bodies.
  const bodyFontSize = 14;

  return (
    <div
      id={id}
      className={className}
      role="note"
      // P-GROUP Phase A (`callout`) — the callout box is ONE fragmentation atom
      // in the paged deck (break-inside: avoid). Kills the live "GRID QUEUE
      // callout fragmented across the p4|p5 seam and DUPLICATED on both pages"
      // class: pagedjs re-rendered the box whole on the next page while leaving
      // the fragment behind. A callout is a bounded note (a label + a few
      // lines), so the atom can never exceed a page.
      data-layout-group="callout"
      data-paged-split-after={pagedSplitAfter ? 'true' : undefined}
      style={{
        // William QA 2026-06-28 — left accent rules removed report-wide. The
        // callout reads via its tinted fill (named tones) + spacing alone; the
        // box is fully rounded rather than a left-flat tab.
        background,
        borderRadius: named ? '3px' : undefined,
        padding,
        ...style,
      }}
    >
      {label != null && (
        <div
          style={{
            fontFamily: MONO,
            // 12px label/caption token (text-xs) — unified across editorial labels.
            fontSize: REPORT_TEXT_PX.label,
            fontWeight: 600,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: ruleColor,
            marginBottom: dense ? 4 : 6,
          }}
        >
          {label}
        </div>
      )}
      <div style={{ color: roleColor(theme, 'body'), fontSize: bodyFontSize, lineHeight: 1.6 }}>
        {children}
      </div>
    </div>
  );
}
